<template>
  <div v-if="visible" class="cp-overlay" @click.self="close">
    <div class="cp-modal">
      <!-- Header -->
      <div class="cp-header">
        <div>
          <h3>AI Studio 제작 상담</h3>
          <p>프로젝트에 대해 알려주시면, 최적의 제작 방향을 안내드립니다.</p>
        </div>
        <button class="cp-close" @click="close"><i class="fal fa-times"></i></button>
      </div>

      <!-- Body -->
      <div class="cp-body">
        <!-- Step indicator -->
        <div class="cp-steps">
          <div class="cp-step" :class="{ active: step === 1, done: step > 1 }" @click="step > 1 && (step = 1)">
            <span class="cp-step-num">1</span>
            <span class="cp-step-label">프로젝트 정보</span>
          </div>
          <div class="cp-step-line" :class="{ active: step > 1 }"></div>
          <div class="cp-step" :class="{ active: step === 2 }">
            <span class="cp-step-num">2</span>
            <span class="cp-step-label">연락처 정보</span>
          </div>
        </div>

        <!-- Step 1: 프로젝트 정보 -->
        <div v-show="step === 1" class="cp-step-content">
          <!-- 제작 플랜 선택 -->
          <div class="cp-field">
            <label>관심 플랜</label>
            <div class="cp-plan-group">
              <div
                v-for="plan in plans"
                :key="plan.value"
                class="cp-plan-card"
                :class="{ active: form.plan === plan.value }"
                @click="form.plan = plan.value"
              >
                <strong>{{ plan.label }}</strong>
                <span>{{ plan.price }}</span>
              </div>
            </div>
          </div>

          <!-- 제작 유형 -->
          <div class="cp-field">
            <label>제작 유형 <span class="req">*</span></label>
            <div class="cp-chip-group">
              <div
                v-for="t in platformTypes"
                :key="t.value"
                class="cp-chip"
                :class="{ active: form.platformType === t.value }"
                @click="form.platformType = t.value"
              >{{ t.label }}</div>
            </div>
          </div>

          <!-- 프로젝트명 -->
          <div class="cp-field">
            <label>프로젝트명 / 서비스명</label>
            <input type="text" v-model="form.projectName" placeholder="예: 배달앱, 회원관리 시스템 등" />
          </div>

          <!-- 프로젝트 설명 -->
          <div class="cp-field">
            <label>프로젝트 설명 <span class="req">*</span></label>
            <textarea v-model="form.description" placeholder="만들고자 하는 서비스에 대해 간략히 알려주세요.&#10;예: 어떤 서비스인지, 주요 기능, 참고 사이트/앱 등" rows="4"></textarea>
          </div>

          <!-- 예산 범위 -->
          <div class="cp-field">
            <label>예산 범위</label>
            <select v-model="form.budget">
              <option value="">선택해 주세요</option>
              <option value="under_500">500만 원 이하</option>
              <option value="500_1000">500만 ~ 1,000만 원</option>
              <option value="1000_2000">1,000만 ~ 2,000만 원</option>
              <option value="over_2000">2,000만 원 이상</option>
              <option value="undecided">미정 / 상담 후 결정</option>
            </select>
          </div>

          <!-- 희망 일정 -->
          <div class="cp-field">
            <label>희망 런칭 시기</label>
            <select v-model="form.timeline">
              <option value="">선택해 주세요</option>
              <option value="asap">최대한 빠르게</option>
              <option value="1month">1개월 이내</option>
              <option value="2_3month">2~3개월 이내</option>
              <option value="flexible">여유 있음</option>
              <option value="undecided">미정</option>
            </select>
          </div>

          <!-- 첨부파일 -->
          <div class="cp-field">
            <label>참고 자료 <span class="optional">(선택)</span></label>
            <div class="cp-file-area">
              <button class="cp-file-btn" @click="triggerFileInput">
                <i class="fal fa-paperclip"></i> 파일 첨부
              </button>
              <span class="cp-file-name" v-if="uploadedFileName">{{ uploadedFileName }}
                <i class="fal fa-times" style="cursor:pointer; margin-left:4px;" @click="clearFile"></i>
              </span>
              <span class="cp-file-hint" v-else>기획서, 참고 화면 등 (최대 10MB)</span>
              <input type="file" ref="fileInput" style="display:none" @change="onFileSelected" />
            </div>
          </div>

          <button class="cp-next-btn" @click="goStep2">
            다음 단계 <i class="fal fa-arrow-right"></i>
          </button>
        </div>

        <!-- Step 2: 연락처 정보 -->
        <div v-show="step === 2" class="cp-step-content">
          <div class="cp-field">
            <label>상담 유형</label>
            <div class="cp-chip-group">
              <div class="cp-chip" :class="{ active: form.consultingType === 'P' }" @click="form.consultingType = 'P'">개인</div>
              <div class="cp-chip" :class="{ active: form.consultingType === 'E' }" @click="form.consultingType = 'E'">기업 / 단체</div>
            </div>
          </div>

          <div class="cp-row">
            <div class="cp-field cp-half">
              <label>이름 <span class="req">*</span></label>
              <input type="text" v-model="form.name" placeholder="담당자 이름" />
            </div>
            <div class="cp-field cp-half">
              <label>연락처 <span class="req">*</span></label>
              <input type="tel" v-model="form.phone" placeholder="010-0000-0000" />
            </div>
          </div>

          <div class="cp-field">
            <label>이메일 <span class="req">*</span></label>
            <input type="email" v-model="form.email" placeholder="example@email.com" />
          </div>

          <div class="cp-field" v-if="form.consultingType === 'E'">
            <label>회사명</label>
            <input type="text" v-model="form.company" placeholder="회사/단체명" />
          </div>

          <!-- 요약 카드 -->
          <div class="cp-summary">
            <h5>상담 요약</h5>
            <div class="cp-summary-row" v-if="form.plan">
              <span>플랜</span><strong>{{ planLabel }}</strong>
            </div>
            <div class="cp-summary-row" v-if="form.platformType">
              <span>유형</span><strong>{{ platformLabel }}</strong>
            </div>
            <div class="cp-summary-row" v-if="form.projectName">
              <span>프로젝트</span><strong>{{ form.projectName }}</strong>
            </div>
            <div class="cp-summary-row" v-if="form.budget">
              <span>예산</span><strong>{{ budgetLabel }}</strong>
            </div>
          </div>

          <div class="cp-btn-row">
            <button class="cp-back-btn" @click="step = 1">
              <i class="fal fa-arrow-left"></i> 이전
            </button>
            <button class="cp-submit-btn" :disabled="isSubmitting" @click="submitForm">
              <span v-if="isSubmitting">전송 중...</span>
              <span v-else>상담 신청하기</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading overlay -->
      <div v-if="isSubmitting" class="cp-loading">
        <div class="cp-spinner"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedPlan: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      step: 1,
      form: {
        plan: '',
        platformType: '',
        projectName: '',
        description: '',
        budget: '',
        timeline: '',
        consultingType: 'P',
        name: '',
        phone: '',
        email: '',
        company: ''
      },
      plans: [
        { value: 'mvp', label: 'MVP 제작', price: '300만~700만 원' },
        { value: 'launch', label: '서비스 런칭형', price: '800만~1,500만 원' },
        { value: 'advanced', label: '고도화 / 운영형', price: '별도 협의' }
      ],
      platformTypes: [
        { value: 'app', label: '앱' },
        { value: 'web', label: '웹' },
        { value: 'both', label: '웹 + 앱' },
        { value: 'unsure', label: '잘 모르겠어요' }
      ],
      uploadedFileId: null,
      uploadedFilePath: null,
      uploadedFileName: '',
      publicKey: null,
      isSubmitting: false,
      recaptchaReady: false
    }
  },
  computed: {
    planLabel() {
      const p = this.plans.find(p => p.value === this.form.plan)
      return p ? p.label + ' (' + p.price + ')' : ''
    },
    platformLabel() {
      const t = this.platformTypes.find(t => t.value === this.form.platformType)
      return t ? t.label : ''
    },
    budgetLabel() {
      const map = {
        'under_500': '500만 원 이하',
        '500_1000': '500만 ~ 1,000만 원',
        '1000_2000': '1,000만 ~ 2,000만 원',
        'over_2000': '2,000만 원 이상',
        'undecided': '미정 / 상담 후 결정'
      }
      return map[this.form.budget] || ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        if (this.selectedPlan) {
          this.form.plan = this.selectedPlan
        }
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  mounted() {
    this.loadPublicKey()
    this.loadRecaptcha()
    this.loadEncryptionLibs()
  },
  methods: {
    close() {
      this.$emit('close')
    },

    goStep2() {
      if (!this.form.platformType) {
        alert('제작 유형을 선택해 주세요.')
        return
      }
      if (!this.form.description) {
        alert('프로젝트 설명을 입력해 주세요.')
        return
      }
      this.step = 2
    },

    loadEncryptionLibs() {
      if (!window.JSEncrypt) {
        const script = document.createElement('script')
        script.src = '//cdnjs.cloudflare.com/ajax/libs/jsencrypt/3.3.2/jsencrypt.min.js'
        document.head.appendChild(script)
      }
      if (!window.CryptoJS) {
        const script = document.createElement('script')
        script.src = '//cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js'
        document.head.appendChild(script)
      }
    },

    loadPublicKey() {
      axios.get('/auth/public-key')
        .then(response => {
          if (response.data && response.data.status === 'success') {
            this.publicKey = response.data.publicKey
          }
        })
        .catch(() => {})
    },

    loadRecaptcha() {
      if (document.querySelector('script[src*="recaptcha"]')) {
        this.recaptchaReady = true
        return
      }
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?render=6LcmoGkqAAAAAPf-gOm7csf7EZ20TGFS-3s-E4d8'
      script.onload = () => { this.recaptchaReady = true }
      document.head.appendChild(script)
    },

    executeRecaptcha() {
      return new Promise((resolve) => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(function() {
            window.grecaptcha.execute('6LcmoGkqAAAAAPf-gOm7csf7EZ20TGFS-3s-E4d8', { action: 'contact' })
              .then(token => resolve(token))
              .catch(() => resolve(null))
          })
        } else {
          resolve(null)
        }
      })
    },

    aesEncrypt(data, aesKey) {
      const CryptoJS = window.CryptoJS
      const iv = CryptoJS.lib.WordArray.random(16)
      const encrypted = CryptoJS.AES.encrypt(data, aesKey, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })
      const encryptedWithIv = iv.concat(encrypted.ciphertext)
      return CryptoJS.enc.Base64.stringify(encryptedWithIv)
    },

    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    clearFile() {
      this.uploadedFileId = null
      this.uploadedFilePath = null
      this.uploadedFileName = ''
      this.$refs.fileInput.value = ''
    },

    onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        alert('파일 크기는 10MB 이하만 가능합니다.')
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      formData.append('atcKey', 'service_qa_attach')
      formData.append('p_user_info_code', 'sgR25eo0%2FRq5icewMBx8KYHMB0eFJgOfDQBK%2BOxvziQ3IAQnwII6DrwL5ptvjqsm')

      this.isSubmitting = true
      axios.post('/file_management/file_upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then(response => {
          const result = response.data
          if (result.upload === 'ok') {
            this.uploadedFileId = result.fileId
            this.uploadedFilePath = result.path
            this.uploadedFileName = result.fileName
          }
        })
        .catch(() => {
          alert('파일 업로드에 실패했습니다.')
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },

    async submitForm() {
      if (!this.form.name) { alert('이름을 입력해 주세요.'); return }
      if (!this.form.phone) { alert('연락처를 입력해 주세요.'); return }
      if (!this.form.email) { alert('이메일을 입력해 주세요.'); return }

      this.isSubmitting = true

      try {
        const consultingTypeStr = this.form.consultingType === 'P' ? '개인' : '기업/단체'

        let contentText = ''
        contentText += '[AI Studio 제작 상담]<br />'
        contentText += '상담유형 : ' + consultingTypeStr + '<br />'
        if (this.form.plan) contentText += '관심 플랜 : ' + this.planLabel + '<br />'
        if (this.form.platformType) contentText += '제작 유형 : ' + this.platformLabel + '<br />'
        if (this.form.projectName) contentText += '프로젝트명 : ' + this.form.projectName + '<br />'
        contentText += '프로젝트 설명 : <br />' + this.form.description + '<br />'
        if (this.form.budget) contentText += '예산 범위 : ' + this.budgetLabel + '<br />'
        if (this.form.timeline) {
          const timelineMap = { 'asap': '최대한 빠르게', '1month': '1개월 이내', '2_3month': '2~3개월 이내', 'flexible': '여유 있음', 'undecided': '미정' }
          contentText += '희망 런칭 시기 : ' + (timelineMap[this.form.timeline] || '') + '<br />'
        }
        contentText += '<br />'
        contentText += '이름 : ' + this.form.name + '<br />'
        contentText += '이메일 : ' + this.form.email + '<br />'
        contentText += '연락처 : ' + this.form.phone + '<br />'
        if (this.form.company) contentText += '회사명 : ' + this.form.company + '<br />'
        if (this.uploadedFilePath) contentText += '첨부파일 : <br />' + this.uploadedFilePath + '<br />'

        const planPrefix = this.form.plan ? '[' + (this.plans.find(p => p.value === this.form.plan) || {}).label + '] ' : ''
        let tempContent = this.form.description.split('\n')[0]
        let articleSubject = '[AI Studio] ' + planPrefix + (tempContent.length > 30 ? tempContent.substring(0, 30) + '...' : tempContent)

        let inquiryType = 'app_inquiry'
        if (this.form.plan === 'advanced') inquiryType = 'app_function'

        let dataOb = {
          app_id: '8abb576f-24f8-47aa-b6d7-a08471954de6',
          article_subject: articleSubject,
          content_text: contentText,
          file_id: this.uploadedFileId || '0',
          inquiry_type: inquiryType + '_@' + this.form.consultingType + '@',
          phone_num: this.form.phone,
          is_recelve_yn: 'N',
          inquiryName: this.form.name,
          inquiryEmail: this.form.email,
          inquiryContent: this.form.description
        }

        // Encrypt data
        try {
          const CryptoJS = window.CryptoJS
          const JSEncrypt = window.JSEncrypt
          if (CryptoJS && JSEncrypt && this.publicKey) {
            const aesKey = CryptoJS.lib.WordArray.random(32)

            if (dataOb.inquiryName) dataOb.inquiryName = this.aesEncrypt(dataOb.inquiryName, aesKey)
            if (dataOb.inquiryEmail) dataOb.inquiryEmail = this.aesEncrypt(dataOb.inquiryEmail, aesKey)
            if (dataOb.inquiryContent) dataOb.inquiryContent = this.aesEncrypt(dataOb.inquiryContent, aesKey)
            if (dataOb.content_text) dataOb.content_text = this.aesEncrypt(dataOb.content_text, aesKey)
            if (dataOb.phone_num) dataOb.phone_num = this.aesEncrypt(dataOb.phone_num, aesKey)

            const aesKeyBase64 = CryptoJS.enc.Base64.stringify(aesKey)
            const rsaEncrypt = new JSEncrypt()
            rsaEncrypt.setPublicKey(this.publicKey)
            const encryptedAESKey = rsaEncrypt.encrypt(aesKeyBase64)
            dataOb.encrypted_aes_key = encryptedAESKey
          }
        } catch (e) {
          // encryption failed, send as-is
        }

        // reCAPTCHA
        const recaptchaToken = await this.executeRecaptcha()
        if (recaptchaToken) {
          dataOb.grecaptcha_code = recaptchaToken
        }

        const formData = new URLSearchParams()
        for (const key in dataOb) {
          if (dataOb[key] !== null && dataOb[key] !== undefined) {
            formData.append(key, dataOb[key])
          }
        }

        const response = await axios.post('/main/insert_service_qa_item', formData, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        })

        if (response.data && response.data.result > 0) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'complete_contact_on_landing',
            query_type: inquiryType,
            query_callback_type: consultingTypeStr,
            query_plan: this.form.plan || 'none'
          })

          alert('상담 신청이 접수되었습니다.\n빠른 시간 내에 연락드리겠습니다.')
          this.resetForm()
          this.close()
        } else {
          alert('접수에 실패했습니다. 잠시 후 다시 시도해 주세요.')
        }
      } catch (e) {
        alert('오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      this.step = 1
      this.form = {
        plan: '',
        platformType: '',
        projectName: '',
        description: '',
        budget: '',
        timeline: '',
        consultingType: 'P',
        name: '',
        phone: '',
        email: '',
        company: ''
      }
      this.uploadedFileId = null
      this.uploadedFilePath = null
      this.uploadedFileName = ''
    }
  }
}
</script>

<style scoped>
.cp-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cp-modal {
  background: #fff;
  border-radius: 16px;
  width: 92%;
  max-width: 600px;
  max-height: 92vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.25);
}

/* Header */
.cp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 32px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.cp-header h3 {
  font-size: 22px;
  font-weight: 700;
  color: #0e1133;
  margin: 0 0 4px;
}
.cp-header p {
  font-size: 14px;
  color: #7a7a7a;
  margin: 0;
}
.cp-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #aaa;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  margin-left: 12px;
}
.cp-close:hover { color: #333; }

/* Body */
.cp-body {
  padding: 24px 32px 28px;
}

/* Steps */
.cp-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}
.cp-step {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
}
.cp-step.done { cursor: pointer; }
.cp-step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e8e8e8;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.2s;
}
.cp-step.active .cp-step-num,
.cp-step.done .cp-step-num {
  background: #0a44b9;
  color: #fff;
}
.cp-step-label {
  font-size: 14px;
  color: #999;
  font-weight: 500;
}
.cp-step.active .cp-step-label,
.cp-step.done .cp-step-label {
  color: #0e1133;
}
.cp-step-line {
  width: 40px;
  height: 2px;
  background: #e8e8e8;
  margin: 0 12px;
  transition: background 0.2s;
}
.cp-step-line.active { background: #0a44b9; }

/* Fields */
.cp-field {
  margin-bottom: 18px;
}
.cp-field label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #0e1133;
  margin-bottom: 6px;
}
.cp-field label .req { color: #0a44b9; }
.cp-field label .optional { color: #aaa; font-weight: 400; }

.cp-field input[type="text"],
.cp-field input[type="email"],
.cp-field input[type="tel"],
.cp-field select,
.cp-field textarea {
  width: 100%;
  border: 1px solid #d5d5d5;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: #fff;
}
.cp-field input:focus,
.cp-field select:focus,
.cp-field textarea:focus {
  border-color: #0a44b9;
}
.cp-field select { height: 44px; }
.cp-field textarea {
  resize: vertical;
  min-height: 90px;
}

/* Plan cards */
.cp-plan-group {
  display: flex;
  gap: 10px;
}
.cp-plan-card {
  flex: 1;
  text-align: center;
  padding: 14px 8px;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
}
.cp-plan-card strong {
  display: block;
  font-size: 14px;
  color: #0e1133;
  margin-bottom: 4px;
}
.cp-plan-card span {
  display: block;
  font-size: 12px;
  color: #7a7a7a;
}
.cp-plan-card:hover {
  border-color: #b0c4f5;
}
.cp-plan-card.active {
  border-color: #0a44b9;
  background: #f0f4ff;
}
.cp-plan-card.active strong { color: #0a44b9; }

/* Chips */
.cp-chip-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.cp-chip {
  padding: 9px 18px;
  border: 1px solid #d5d5d5;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  color: #505056;
  background: #fff;
}
.cp-chip:hover { border-color: #0a44b9; color: #0a44b9; }
.cp-chip.active {
  background: #0a44b9;
  border-color: #0a44b9;
  color: #fff;
  font-weight: 600;
}

/* File */
.cp-file-area {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cp-file-btn {
  background: #f5f5f7;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px;
  cursor: pointer;
  color: #505056;
  white-space: nowrap;
  transition: background 0.2s;
}
.cp-file-btn:hover { background: #eaeaec; }
.cp-file-name {
  font-size: 13px;
  color: #0a44b9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp-file-hint {
  font-size: 13px;
  color: #aaa;
}

/* Row layout */
.cp-row {
  display: flex;
  gap: 12px;
}
.cp-half { flex: 1; }

/* Buttons */
.cp-next-btn {
  width: 100%;
  background: #0a44b9;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 8px;
}
.cp-next-btn:hover { background: #083999; }

.cp-btn-row {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.cp-back-btn {
  flex: 0 0 auto;
  background: #f5f5f7;
  color: #505056;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.cp-back-btn:hover { background: #eaeaec; }
.cp-submit-btn {
  flex: 1;
  background: #0a44b9;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.cp-submit-btn:hover { background: #083999; }
.cp-submit-btn:disabled {
  background: #bbb;
  cursor: not-allowed;
}

/* Summary */
.cp-summary {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 18px;
}
.cp-summary h5 {
  font-size: 13px;
  font-weight: 600;
  color: #7a7a7a;
  margin: 0 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.cp-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
}
.cp-summary-row span { color: #7a7a7a; }
.cp-summary-row strong { color: #0e1133; font-weight: 600; }

/* Loading */
.cp-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}
.cp-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e8e8e8;
  border-top-color: #0a44b9;
  border-radius: 50%;
  animation: cp-spin 0.7s linear infinite;
}
@keyframes cp-spin {
  to { transform: rotate(360deg); }
}

/* Mobile */
@media (max-width: 767px) {
  .cp-modal {
    width: 96%;
    max-height: 96vh;
    border-radius: 14px;
  }
  .cp-header, .cp-body {
    padding-left: 20px;
    padding-right: 20px;
  }
  .cp-plan-group {
    flex-direction: column;
  }
  .cp-row {
    flex-direction: column;
    gap: 0;
  }
  .cp-step-label {
    font-size: 12px;
  }
}
</style>
